/**
 * Pure copy and presentation helpers for the five modals (README §5;
 * docs/design-handoff/reference/communicatorv2.3.md §3 and §10). Nothing
 * here fetches or calls an operation — see ./reads.ts for the read seams and
 * the modal components themselves for the one `ops.call` each makes.
 *
 * Literal copy is written to say the four ideas the brief names explicitly
 * (linking-is-confirmation, the Matched ceiling, OTP-to-destination-of-record,
 * close-is-terminal) rather than paraphrase them loosely, since several of
 * the required tests assert on this copy.
 */
import type { Channel, ClientDisclosure } from '@communicator/shared'
import { slaLabelFromSeconds } from '../context/format'

/** §3 "Human action upgrades trust… The consequential act carries the confirmation. No separate
 * 'yes it's them' click." — the attach-ticket modal states this plainly (idea #1). */
export const ATTACH_IS_CONFIRMATION_TEXT =
  "Attaching a ticket is the human confirmation itself — there's no separate \"yes, it's them\" step anywhere in this product. Linking is the trust decision, not filing."

/** §3 "Matched is who this probably is, never what they are allowed to ask for… The ceiling for a
 * handle is Matched, unless OTP challenges it." (idea #2). */
export const MATCH_CEILING_TEXT =
  'Matched is who this probably is, never what they may ask for — a strong hint, not proof. Matched is the ceiling for this handle unless a one-time code lifts it to OTP-Verified.'

/** §3 "OTP always goes to the destination of record on the Halo contact, never to a handle the
 * requester supplies or a caller ID presents." (idea #3). Shown when the thread is not yet Matched,
 * since OTP has nowhere-of-record to send to below that tier. */
export const OTP_NEEDS_MATCHED_TEXT =
  'A one-time code always goes to the destination of record on a Matched Halo contact — never to a handle the requester supplied. This thread has no Matched contact yet, so there is nowhere of record to send a code.'

/** §10 "Close is terminal. A closed thread never reopens." (idea #4). */
export const CLOSE_TERMINAL_TEXT = 'Close is terminal — a closed thread never reopens. A returning client starts a fresh thread and ticket.'

/** §10 "Unlinked: pure disposal, nothing to Halo. The telemarketer path." (idea #4). */
export const CLOSE_UNLINKED_TEXT = 'No ticket is linked, so nothing about this thread is written to Halo. This is pure disposal.'

/**
 * How confident this candidate is, and why — per candidate, not per result set.
 *
 * A person whose own mobile this is sits in the same list as four colleagues
 * who merely share a switchboard. Labelling both "shared line" told a
 * dispatcher nothing about which name to pick.
 */
export const REASON_LABEL: Record<'number-on-contact' | 'shared-line' | 'other-field', string> = {
  'number-on-contact': 'their number',
  'shared-line': 'company main line',
  'other-field': 'matched elsewhere',
}

/** Green for a direct hit, amber for a whole-company guess. */
export const REASON_TONE: Record<'number-on-contact' | 'shared-line' | 'other-field', 'ok' | 'warn' | 'info'> = {
  'number-on-contact': 'ok',
  'shared-line': 'warn',
  'other-field': 'info',
}

/**
 * Fix round 1 (code review): "not wired / could not reach Halo" and "wired, and Halo genuinely has
 * nothing" are different facts a dispatcher acts on differently — the first means wait and retry,
 * the second is a real finding (create a contact, this contact has no destination on file, this
 * client has no other open ticket). Collapsing both to one empty-looking state risks a duplicate
 * contact, a wrong "go fix the contact record" detour, or a duplicate ticket. Every "empty" render
 * in the Match, OTP and Attach-ticket modals therefore branches on `reads.ts`'s `wired` flag and
 * uses one of the paired constants below — never a shared string.
 */
export const HALO_UNREACHABLE_CANDIDATES_TEXT =
  'Could not reach Halo to look up candidates for this handle. This says nothing about whether a match exists — try again once Halo answers.'
export const NO_CANDIDATES_TEXT = 'No Halo candidates matched this handle.'

export const HALO_UNREACHABLE_DESTINATIONS_TEXT =
  "Could not reach Halo to look up this contact's destinations of record. This says nothing about whether one is on file — try again once Halo answers."
export const NO_DESTINATIONS_TEXT = 'This contact has no destination of record on file in Halo.'

export const HALO_UNREACHABLE_RECENT_TICKETS_TEXT = "Could not reach Halo to look up this client's recent tickets."
export const NO_RECENT_TICKETS_TEXT = 'No recent tickets found for this client.'

/** ComposeModal's own muted line for its recent-tickets list (task 2) — distinct copy from the
 * attach-ticket modal's `unwiredText('tickets', …)` above: the composer still works with no
 * ticket at all, so this is deliberately quieter than an alert and never routes through the
 * error banner (`compose-modal-error`). Shown only for `halo-unreachable`/`not-configured`; a
 * `no-client` reason or a genuinely empty list shows nothing at all — an empty-looking composer
 * is the ordinary "send with no ticket" case, not a finding worth a line of copy. */
export const COMPOSE_RECENT_TICKETS_UNAVAILABLE_TEXT = 'Recent tickets could not be loaded.'

// ticketSubjectFor used to live here, composing "<who> — <channel> conversation"
// because "the attach modal never asks the dispatcher to type one." Decided with
// Andrew 2026-09-01: it now does ask — every ticket was arriving in Halo with one
// of two canned summaries, which the team read as noise. The dispatcher's typed
// summary is the subject; Quick Fix (CallSummary.tsx) keeps its own one-click one.

/** Accepts a ticket number typed with or without a leading '#' (README §5: "type a ticket number
 * (with or without #, Enter or Link)"). Returns null for anything that is not plain digits once a
 * single leading '#' is stripped, so the caller can refuse to submit garbage rather than send it to
 * the operations layer. */
export function normalizeTicketInput(raw: string): string | null {
  const trimmed = raw.trim().replace(/^#/, '')
  if (!/^\d+$/.test(trimmed)) return null
  return `#${trimmed}`
}

export const HANDOFF_CUE_ORDER: readonly ClientDisclosure[] = ['now', 'sla-range', 'exact-time', 'none']

export const HANDOFF_CUE_LABEL: Record<ClientDisclosure, string> = {
  now: 'Handing off now',
  'sla-range': 'Within SLA (timing vague on purpose)',
  'exact-time': 'At a specific time',
  none: 'Nothing sent to the client',
}

/**
 * The exact three example sentences from §10, plus the fourth ("nothing
 * sent") the README's modal bullet calls out by name. The SLA phrase always
 * comes from the thread's own `slaTargetS` via `slaLabelFromSeconds` (context
 * pane's own helper, reused rather than duplicated) — never a typed-in
 * number, per the global constraint.
 */
export function handoffCueText(
  disclosure: ClientDisclosure,
  techName: string,
  slaTargetS: number,
  exactTime: string,
): string {
  switch (disclosure) {
    case 'none':
      return 'Nothing sent to the client.'
    case 'now':
      return `Handing off now to ${techName}.`
    case 'sla-range':
      return `Your ticket's been handed to ${techName}, who'll reach out within ${slaLabelFromSeconds(slaTargetS)}.`
    case 'exact-time':
      return `Your ticket's been handed to ${techName}, who'll reach out at ${exactTime || 'the scheduled time'}.`
  }
}

/** Sentinel value for `store.closeCue` meaning "tell the client it's resolved" — the only
 * client-facing option besides "nothing sent" this build offers (§10: "chosen and suppressible,
 * same shape as the handoff cues"). Kept generic and non-fabricated: it claims nothing about the
 * specific fix, only that the ticket is resolved on our end. */
export const CLOSE_CUE_RESOLVED = 'resolved'
export const CLOSE_CUE_RESOLVED_TEXT = "Thanks — this is resolved on our end."

/** The generic fallback shown when a caught error is not an `OpsError` (a real bug, not a layer
 * refusal) — matches the phrase ThreadPane already uses for the same distinction. */
export const GENERIC_OP_ERROR = 'Something went wrong. Try again.'


/**
 * The accurate line for each reason a lookup went unanswered.
 *
 * These replace a single "Could not reach Halo" that was shown for every cause,
 * including two where Halo was fine. Telling someone a third-party system is
 * down when it is not is worse than saying nothing — they go and check it. Two
 * of these four are actions the reader can take immediately, so they say so.
 */
const UNWIRED_COPY: Record<string, { candidates: string; destinations: string; tickets: string }> = {
  'halo-unreachable': {
    candidates: HALO_UNREACHABLE_CANDIDATES_TEXT,
    destinations: HALO_UNREACHABLE_DESTINATIONS_TEXT,
    tickets: HALO_UNREACHABLE_RECENT_TICKETS_TEXT,
  },
  'not-configured': {
    candidates: 'Halo is not configured on this deployment, so candidates cannot be looked up. This says nothing about whether a match exists.',
    destinations: 'Halo is not configured on this deployment, so destinations of record cannot be looked up. Sending a code is unavailable until it is.',
    tickets: 'Halo is not configured on this deployment, so recent tickets cannot be listed. You can still type a ticket number above.',
  },
  'not-matched': {
    candidates: HALO_UNREACHABLE_CANDIDATES_TEXT,
    destinations:
      'This conversation is not matched to a Halo contact yet, so there is no destination of record to send a code to. Match it first.',
    tickets: HALO_UNREACHABLE_RECENT_TICKETS_TEXT,
  },
  'no-client': {
    candidates: HALO_UNREACHABLE_CANDIDATES_TEXT,
    destinations: HALO_UNREACHABLE_DESTINATIONS_TEXT,
    tickets:
      'No client is known for this conversation yet, so there are no recent tickets to list. Match it to a contact first, or type a ticket number above.',
  },
}

/**
 * Pick the line for a given reason.
 *
 * Falls back to the Halo-unreachable copy when the server sent no reason — an
 * older build, or a failure that never reached it. That is the conservative
 * default: it tells the reader to retry rather than asserting a finding.
 */
export function unwiredText(kind: 'candidates' | 'destinations' | 'tickets', reason?: string): string {
  const row = (reason && UNWIRED_COPY[reason]) || UNWIRED_COPY['halo-unreachable']
  return row[kind]
}

/**
 * The compose-prefill deep link (finding 4). `GET /ops/tickets/:id/compose-prefill`
 * (thread-reads.ts) answers `wired: false` with one of `'not-found' | 'halo-unreachable' |
 * 'not-configured'` — codes, not prose, same discipline as `UnwiredReason` above — and the two
 * causes need different composer behaviour, not just different copy:
 *
 *   'not-found'  — Halo answered, and this ticket id is not one it has. Sending against it would
 *                  400 on the very next Halo write, so the chip is CLEARED rather than kept.
 *   otherwise    — Halo could not be asked at all (unreachable, or not configured on this
 *                  deployment). The ticket id might still be perfectly good, so the chip STAYS,
 *                  editable same as always.
 *
 * Both cases still open the composer and still say why — never a silent, unexplained empty slot.
 */
export const COMPOSE_PREFILL_NOT_FOUND_TEXT =
  "This ticket could not be found in Halo, so the ticket link has been cleared. Search for the contact instead, or type a different ticket number."
export const COMPOSE_PREFILL_UNREACHABLE_TEXT =
  "Could not reach Halo to look up this ticket's contact. The ticket number is kept — try sending again, or continue without it."

export function composePrefillErrorText(reason: string | undefined): string {
  return reason === 'not-found' ? COMPOSE_PREFILL_NOT_FOUND_TEXT : COMPOSE_PREFILL_UNREACHABLE_TEXT
}
